import type {
  CategoryContent,
  ChainContent,
  ContainsContent,
  ContentItem,
  DualConditionContent,
  ExcludesLetterContent,
  NameThreeContent,
  StartsWithLetterContent,
} from '@juegazo/shared';

// ===== category (50) =====
const CATEGORY_NAMES = [
  'Animales', 'Frutas', 'Colores', 'Países', 'Ciudades',
  'Comidas', 'Bebidas', 'Deportes', 'Instrumentos musicales', 'Profesiones',
  'Objetos de la cocina', 'Ropa', 'Verbos en infinitivo', 'Películas conocidas', 'Marcas de autos',
  'Herramientas', 'Adjetivos', 'Objetos de la casa', 'Partes del cuerpo', 'Plantas',
  'Juguetes', 'Meses del año', 'Días de la semana', 'Cantantes o bandas', 'Superhéroes',
  'Muebles', 'Aves', 'Insectos', 'Vehículos', 'Planetas',
  'Útiles escolares', 'Nombres de hombre', 'Nombres de mujer', 'Cosas de la playa', 'Postres',
  'Cosas que se enchufan', 'Cosas frías', 'Deportes de equipo', 'Animales marinos', 'Cosas de color rojo',
  'Razas de perro', 'Sabores de helado', 'Países de América', 'Idiomas', 'Redes sociales',
  'Electrodomésticos', 'Cosas que vuelan', 'Verduras', 'Juegos de mesa', 'Series de televisión'
];

const CATEGORY_ITEMS: CategoryContent[] = CATEGORY_NAMES.map((category, i) => ({
  id: `cat-${String(i + 1).padStart(3, '0')}`,
  mechanic: 'category',
  enabled: true,
  difficulty: 'easy', // Nueva regla: Una sola restricción = easy
  category,
}));

// ===== starts_with_letter (50) =====
const STARTS_WITH_LETTER_LETTERS = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
  'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'V', 'Z',
  'AB', 'AR', 'CA', 'CO', 'MA', 'PA', 'SA', 'SE', 'TR', 'BA',
  'BE', 'BI', 'BO', 'BU', 'CE', 'CI', 'CR', 'DE', 'DI', 'ES',
  'FL', 'FR', 'GR', 'IN', 'LA', 'LE', 'LI', 'PL', 'PR', 'QUE'
];

const STARTS_WITH_LETTER_ITEMS: StartsWithLetterContent[] = STARTS_WITH_LETTER_LETTERS.map(
  (letter, i) => ({
    id: `swl-${String(i + 1).padStart(3, '0')}`,
    mechanic: 'starts_with_letter',
    enabled: true,
    difficulty: 'easy',
    letter,
  }),
);

// ===== contains (50) =====
const CONTAINS_SEQUENCES = [
  'AN', 'AR', 'CA', 'CE', 'CO', 'DE', 'DI', 'DO', 'EN', 'ER',
  'ES', 'IN', 'LA', 'LO', 'MA', 'NO', 'ON', 'OR', 'PA', 'RE',
  'RO', 'SA', 'SE', 'TA', 'TE', 'TI', 'TO', 'TRA', 'BRE', 'PLA',
  'QUE', 'GUI', 'LLA', 'RRO', 'MENT', 'CION', 'TIO', 'BLA', 'BLE', 'BRA',
  'CHE', 'CHI', 'CHO', 'CHU', 'CLA', 'CLE', 'CRE', 'CRI', 'CRO', 'CRU'
];

const CONTAINS_ITEMS: ContainsContent[] = CONTAINS_SEQUENCES.map((sequence, i) => ({
  id: `con-${String(i + 1).padStart(3, '0')}`,
  mechanic: 'contains',
  enabled: true,
  difficulty: 'easy',
  sequence,
}));

// ===== excludes_letter (50) =====
const EXCLUDES_LETTER_ROWS: Array<{ category: string; letter: string }> = [
  { category: 'Animales sin letra', letter: 'A' }, { category: 'Animales sin letra', letter: 'E' },
  { category: 'Animales sin letra', letter: 'O' }, { category: 'Frutas sin letra', letter: 'A' },
  { category: 'Frutas sin letra', letter: 'E' }, { category: 'Frutas sin letra', letter: 'O' },
  { category: 'Colores sin letra', letter: 'A' }, { category: 'Colores sin letra', letter: 'E' },
  { category: 'Países sin letra', letter: 'A' }, { category: 'Países sin letra', letter: 'O' },
  { category: 'Ciudades sin letra', letter: 'A' }, { category: 'Ciudades sin letra', letter: 'O' },
  { category: 'Comidas sin letra', letter: 'A' }, { category: 'Comidas sin letra', letter: 'E' },
  { category: 'Profesiones sin letra', letter: 'A' }, { category: 'Profesiones sin letra', letter: 'O' },
  { category: 'Deportes sin letra', letter: 'A' }, { category: 'Deportes sin letra', letter: 'E' },
  { category: 'Ropa sin letra', letter: 'A' }, { category: 'Ropa sin letra', letter: 'E' },
  { category: 'Vehículos sin letra', letter: 'A' }, { category: 'Vehículos sin letra', letter: 'O' },
  { category: 'Muebles sin letra', letter: 'A' }, { category: 'Muebles sin letra', letter: 'O' },
  { category: 'Objetos de la casa sin letra', letter: 'A' }, { category: 'Objetos de la casa sin letra', letter: 'O' },
  { category: 'Herramientas sin letra', letter: 'A' }, { category: 'Herramientas sin letra', letter: 'O' },
  { category: 'Partes del cuerpo sin letra', letter: 'A' }, { category: 'Partes del cuerpo sin letra', letter: 'O' },
  { category: 'Animales sin letra', letter: 'I' }, { category: 'Países sin letra', letter: 'E' },
  { category: 'Profesiones sin letra', letter: 'E' }, { category: 'Ropa sin letra', letter: 'O' },
  { category: 'Comidas sin letra', letter: 'O' }, { category: 'Deportes sin letra', letter: 'I' },
  { category: 'Bebidas sin letra', letter: 'A' }, { category: 'Bebidas sin letra', letter: 'E' },
  { category: 'Instrumentos musicales sin letra', letter: 'A' }, { category: 'Instrumentos musicales sin letra', letter: 'O' },
  { category: 'Vehículos sin letra', letter: 'E' }, { category: 'Muebles sin letra', letter: 'E' },
  { category: 'Útiles escolares sin letra', letter: 'A' }, { category: 'Útiles escolares sin letra', letter: 'O' },
  { category: 'Verduras sin letra', letter: 'A' }, { category: 'Verduras sin letra', letter: 'O' },
  { category: 'Juguetes sin letra', letter: 'A' }, { category: 'Juguetes sin letra', letter: 'O' },
  { category: 'Colores sin letra', letter: 'O' }, { category: 'Colores sin letra', letter: 'I' }
];

const EXCLUDES_LETTER_ITEMS: ExcludesLetterContent[] = EXCLUDES_LETTER_ROWS.map(
  (row, i) => ({
    id: `exl-${String(i + 1).padStart(3, '0')}`,
    mechanic: 'excludes_letter',
    enabled: true,
    difficulty: 'medium', // Restricción con negación = medium
    category: row.category,
    letter: row.letter,
  }),
);

// ===== chain (50) =====
const CHAIN_START_WORDS = [
  'CASA', 'SOL', 'LUNA', 'AGUA', 'ARBOL', 'MESA', 'SILLA', 'PERRO', 'GATO', 'PAN',
  'TREN', 'COCHE', 'FLOR', 'PESCA', 'MONTAÑA', 'MARIPOSA', 'TIERRA', 'CIELO', 'NUBE', 'RIO',
  'TRABAJO', 'FUTBOL', 'PUEBLO', 'CIUDAD', 'MUSICA', 'LIBRO', 'AMIGO', 'ABUELO', 'ESCUELA', 'JUEGO',
  'COMPUTADOR', 'CELULAR', 'BOTELLA', 'CAMINO', 'VENTANA', 'PUERTA', 'RELOJ', 'ZAPATO', 'CAMISA', 'PANTALON',
  'SOMBRERO', 'GUITARRA', 'PIANO', 'PELOTA', 'BICICLETA', 'AUTO', 'CAMION', 'BARCO', 'AVION', 'ESTRELLA'
];

const CHAIN_ITEMS: ChainContent[] = CHAIN_START_WORDS.map((startWord, i) => ({
  id: `chn-${String(i + 1).padStart(3, '0')}`,
  mechanic: 'chain',
  enabled: true,
  difficulty: 'easy',
  startWord,
}));

// ===== dual_condition (50) =====
const DUAL_CONDITION_ROWS: Array<{ category: string; letter: string }> = [
  { category: 'Animales', letter: 'P' }, { category: 'Animales', letter: 'M' },
  { category: 'Animales', letter: 'G' }, { category: 'Animales', letter: 'L' },
  { category: 'Frutas', letter: 'M' }, { category: 'Frutas', letter: 'P' },
  { category: 'Frutas', letter: 'N' }, { category: 'Países', letter: 'A' },
  { category: 'Países', letter: 'M' }, { category: 'Países', letter: 'C' },
  { category: 'Ciudades', letter: 'M' }, { category: 'Ciudades', letter: 'B' },
  { category: 'Ciudades', letter: 'S' }, { category: 'Comidas', letter: 'P' },
  { category: 'Comidas', letter: 'A' }, { category: 'Comidas', letter: 'T' },
  { category: 'Profesiones', letter: 'M' }, { category: 'Profesiones', letter: 'P' },
  { category: 'Profesiones', letter: 'A' }, { category: 'Deportes', letter: 'F' },
  { category: 'Deportes', letter: 'B' }, { category: 'Deportes', letter: 'T' },
  { category: 'Objetos de la casa', letter: 'M' }, { category: 'Instrumentos musicales', letter: 'G' },
  { category: 'Instrumentos musicales', letter: 'P' }, { category: 'Ropa', letter: 'P' },
  { category: 'Ropa', letter: 'C' }, { category: 'Verbos en infinitivo', letter: 'C' },
  { category: 'Verbos en infinitivo', letter: 'H' }, { category: 'Herramientas', letter: 'M' },
  { category: 'Animales', letter: 'C' }, { category: 'Animales', letter: 'S' },
  { category: 'Frutas', letter: 'C' }, { category: 'Países', letter: 'S' },
  { category: 'Países', letter: 'P' }, { category: 'Ciudades', letter: 'C' },
  { category: 'Ciudades', letter: 'P' }, { category: 'Comidas', letter: 'C' },
  { category: 'Comidas', letter: 'E' }, { category: 'Profesiones', letter: 'C' },
  { category: 'Profesiones', letter: 'E' }, { category: 'Deportes', letter: 'C' },
  { category: 'Deportes', letter: 'A' }, { category: 'Ropa', letter: 'S' },
  { category: 'Verbos en infinitivo', letter: 'A' }, { category: 'Verbos en infinitivo', letter: 'E' },
  { category: 'Útiles escolares', letter: 'L' }, { category: 'Útiles escolares', letter: 'C' },
  { category: 'Bebidas', letter: 'C' }, { category: 'Colores', letter: 'A' }
];

const DUAL_CONDITION_ITEMS: DualConditionContent[] = DUAL_CONDITION_ROWS.map((row, i) => ({
  id: `dua-${String(i + 1).padStart(3, '0')}`,
  mechanic: 'dual_condition',
  enabled: true,
  difficulty: 'medium', // Dos restricciones combinadas = medium
  category: row.category,
  letter: row.letter,
}));

// ===== name_three (50) =====
const NAME_THREE_CATEGORIES = [
  'Animales', 'Frutas', 'Colores', 'Países', 'Ciudades',
  'Comidas', 'Bebidas', 'Deportes', 'Profesiones', 'Ropa',
  'Objetos de la casa', 'Muebles', 'Verduras', 'Dulces', 'Herramientas',
  'Juguetes', 'Vehículos', 'Aves', 'Insectos', 'Meses',
  'Instrumentos musicales', 'Cantantes', 'Bailes', 'Juegos', 'Marcas',
  'Películas', 'Superhéroes', 'Partes del cuerpo', 'Adjetivos', 'Verbos',
  'Cosas que vuelan', 'Cosas de la playa', 'Postres', 'Idiomas', 'Razas de perro',
  'Animales marinos', 'Cosas que se enchufan', 'Nombres de hombre', 'Nombres de mujer', 'Países de América',
  'Útiles escolares', 'Deportes de equipo', 'Redes sociales', 'Electrodomésticos', 'Juegos de mesa',
  'Sabores de helado', 'Series de televisión', 'Creadores de contenido', 'Aplicaciones móviles', 'Cosas frías'
];

const NAME_THREE_ITEMS: NameThreeContent[] = NAME_THREE_CATEGORIES.map((category, i) => ({
  id: `n3-${String(i + 1).padStart(3, '0')}`,
  mechanic: 'name_three',
  enabled: true,
  difficulty: 'easy',
  category,
}));

export const CONTENT_CATALOG: ContentItem[] = [
  ...CATEGORY_ITEMS,
  ...STARTS_WITH_LETTER_ITEMS,
  ...CONTAINS_ITEMS,
  ...EXCLUDES_LETTER_ITEMS,
  ...CHAIN_ITEMS,
  ...DUAL_CONDITION_ITEMS,
  ...NAME_THREE_ITEMS,
];